<template>
  <component :is="inline === true ? 'div' : 'dialog'" :open="inline === true ? undefined : visible === true" :class="inline === true ? 'border border-base-300 rounded-box mt-4' : 'modal modal-bottom sm:modal-middle'">
    <div :class="inline === true ? 'flex flex-col' : 'modal-box max-w-4xl flex flex-col max-h-[90vh] p-0'">
      <div class="shrink-0 px-6 pt-6 pb-4 border-b border-base-300">
        <div class="flex items-start justify-between mb-4">
          <div>
            <h3 class="font-bold text-lg">{{ t('aiChatAgentSetup.advanced.rulesFineTuningModalTitle', 'AI Rules Fine Tuning') }}</h3>
            <p class="text-sm text-gray-500 mt-1">{{ t('aiChatAgentSetup.advanced.rulesFineTuningModalDescription', 'The AI is creating procedural rules that guide source usage without copying answer data.') }}</p>
          </div>
          <button v-if="inline !== true" type="button" class="btn btn-sm btn-circle btn-ghost" @click="emit('close')">✕</button>
        </div>

        <div v-if="job !== null">
        <div class="flex items-center justify-between mb-2 text-sm">
          <span class="font-medium">{{ stageLabel }}</span>
          <span>{{ progressPercent }}%</span>
        </div>
        <progress class="progress progress-success w-full" :value="progressPercent" max="100"></progress>
        </div>
      </div>

      <div class="flex-1 min-h-0 overflow-y-auto px-6 py-4">
      <div v-if="error.length > 0" class="alert alert-error text-sm mb-4">
        {{ error }}
      </div>

      <div v-if="missingKnowledgeRows.length > 0" class="alert alert-warning text-sm mb-4">
        <div>
          <div class="font-medium">{{ t('aiChatAgentSetup.advanced.rulesFineTuningMissingKnowledgeTitle', 'Some bad responses do not have supporting knowledge') }}</div>
          <div>{{ t('aiChatAgentSetup.advanced.rulesFineTuningMissingKnowledgeDescription', 'Export these questions to Excel so the missing knowledge can be added before generating rules.') }} {{ missingKnowledgeRows.length }}</div>
        </div>
        <button type="button" class="btn btn-sm ml-3" @click="emit('export-missing-knowledge')">
          {{ t('aiChatAgentSetup.advanced.rulesFineTuningExportMissingKnowledge', 'Export Excel') }}
        </button>
      </div>

      <div v-if="failureSummaryEntries.length > 0" class="alert alert-info text-sm mb-4">
        <div>
          <div class="font-medium">{{ t('aiChatAgentSetup.advanced.rulesFineTuningFailureSummaryTitle', 'Rule failure reasons') }}</div>
          <div v-for="entry in failureSummaryEntries" :key="entry.key" class="mt-1">
            {{ entry.label }}: {{ entry.count }}
          </div>
        </div>
      </div>

      <div v-if="diagnosticsLogFile.length > 0" class="alert alert-info text-sm mb-4">
        <div>
          <div class="font-medium">{{ t('aiChatAgentSetup.advanced.rulesFineTuningLogFileTitle', 'Diagnostics log file') }}</div>
          <div class="break-all">{{ diagnosticsLogFile }}</div>
        </div>
      </div>

      <div v-if="job !== null" class="grid grid-cols-1 md:grid-cols-5 mb-4">
        <div class="p-3 bg-base-200 rounded-box mr-0 md:mr-3 mb-3 md:mb-0">
          <div class="text-xs text-gray-500">{{ t('aiChatAgentSetup.advanced.rulesFineTuningQuestions', 'Questions') }}</div>
          <div class="text-xl font-semibold">{{ processedQuestions }} / {{ totalQuestions }}</div>
        </div>
        <div class="p-3 bg-base-200 rounded-box mr-0 md:mr-3 mb-3 md:mb-0">
          <div class="text-xs text-gray-500">{{ t('aiChatAgentSetup.advanced.rulesFineTuningCreated', 'Created rules') }}</div>
          <div class="text-xl font-semibold">{{ createdRules }}</div>
        </div>
        <div class="p-3 bg-base-200 rounded-box mr-0 md:mr-3 mb-3 md:mb-0">
          <div class="text-xs text-gray-500">{{ t('aiChatAgentSetup.advanced.rulesFineTuningSkipped', 'Skipped') }}</div>
          <div class="text-xl font-semibold">{{ skippedRules }}</div>
        </div>
        <div class="p-3 bg-base-200 rounded-box mr-0 md:mr-3 mb-3 md:mb-0">
          <div class="text-xs text-gray-500">{{ t('aiChatAgentSetup.advanced.rulesFineTuningApproved', 'Approved rules') }}</div>
          <div class="text-xl font-semibold">{{ approvedRulesCount }}</div>
        </div>
        <div class="p-3 bg-base-200 rounded-box mb-3 md:mb-0">
          <div class="text-xs text-gray-500">{{ t('aiChatAgentSetup.advanced.rulesFineTuningAttempt', 'Attempt') }}</div>
          <div class="text-xl font-semibold">{{ currentAttempt }}</div>
        </div>
      </div>

      <div v-if="job !== null" class="mb-4">
        <div class="text-sm font-medium mb-1">{{ t('aiChatAgentSetup.advanced.rulesFineTuningCurrentQuestion', 'Current question') }}</div>
        <div class="p-3 bg-white border border-base-300 rounded-box text-sm whitespace-pre-wrap">{{ currentQuestion }}</div>
      </div>

      <div v-if="job !== null" class="mb-4">
        <div class="text-sm font-medium mb-1">{{ t('aiChatAgentSetup.advanced.rulesFineTuningCurrentRule', 'Rule being tested') }}</div>
        <div class="p-3 bg-white border border-base-300 rounded-box text-sm whitespace-pre-wrap">{{ currentRule }}</div>
      </div>

      <div v-if="currentTests.length > 0" class="mb-4">
        <div class="text-sm font-medium mb-2">{{ t('aiChatAgentSetup.advanced.rulesFineTuningTests', 'Rule tests') }}</div>
        <div v-for="test in currentTests" :key="test.name" class="flex items-start mb-2 text-sm">
          <span class="badge mr-2" :class="test.passed === true ? 'badge-success' : 'badge-error'">{{ test.passed === true ? t('common.pass', 'Pass') : t('common.fail', 'Fail') }}</span>
          <span>
            <span>{{ test.name }}</span>
            <span v-if="typeof test.reason === 'string' && test.reason.length > 0" class="block text-xs text-gray-500 mt-1">{{ test.reason }}</span>
          </span>
        </div>
      </div>

      <div v-if="diagnosticItems.length > 0" class="mb-4">
        <div class="text-sm font-medium mb-2">{{ t('aiChatAgentSetup.advanced.rulesFineTuningDiagnosticsTitle', 'Collected rule tries') }}</div>
        <div v-for="item in diagnosticItems" :key="item.key" class="p-3 bg-base-200 rounded-box text-xs mb-2">
          <div class="font-medium mb-1">{{ item.title }}</div>
          <div class="whitespace-pre-wrap">{{ item.detail }}</div>
        </div>
      </div>

      <div v-if="pendingRules.length > 0" class="mb-4">
        <div class="flex items-center justify-between mb-2">
          <div class="text-sm font-medium">{{ t('aiChatAgentSetup.advanced.rulesFineTuningGeneratedRules', 'Generated rules waiting for approval') }}</div>
          <button type="button" class="btn btn-sm" @click="approveRules(pendingRules)">
            {{ t('aiChatAgentSetup.advanced.rulesFineTuningApproveAll', 'Approve all') }}
          </button>
        </div>
        <div v-for="rule in pendingRules" :key="rule" class="p-3 bg-base-200 rounded-box text-sm mb-2">
          <div class="whitespace-pre-wrap mb-3">{{ rule }}</div>
          <button type="button" class="btn btn-sm" @click="approveRules([rule])">
            {{ t('aiChatAgentSetup.advanced.rulesFineTuningApproveRule', 'Approve rule') }}
          </button>
        </div>
      </div>

      <div v-if="approvedRulesCount > 0" class="alert alert-success text-sm mb-4">
        {{ t('aiChatAgentSetup.advanced.rulesFineTuningApprovedNotice', 'Approved rules were added to the local Rules tab. Click Save or Save & Close to persist them.') }}
      </div>
      </div>

      <div v-if="inline !== true" class="shrink-0 px-6 pb-6 pt-2 border-t border-base-300 modal-action mt-0">
        <button type="button" class="btn" :disabled="isRunning === true" @click="emit('close')">
          {{ isRunning === true ? t('aiChatAgentSetup.advanced.rulesFineTuningWorking', 'Working...') : t('aiChatAgentSetup.buttons.close', 'Close') }}
        </button>
      </div>
    </div>
    <form v-if="inline !== true" method="dialog" class="modal-backdrop bg-black bg-opacity-50" @click.prevent.stop="emit('close')">
      <button>close</button>
    </form>
  </component>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from '@/i18n';

const props = defineProps({
  visible: { type: Boolean, default: false },
  inline: { type: Boolean, default: false },
  job: { type: Object, default: null },
  isRunning: { type: Boolean, default: false },
  error: { type: String, default: '' }
});

const emit = defineEmits(['close', 'approve-rules', 'export-missing-knowledge']);
const { t } = useI18n();
const approvedRules = ref([]);

const totalQuestions = computed(() => {
  const value = Number(props.job?.totalQuestions);
  return Number.isFinite(value) === true && value >= 0 ? Math.floor(value) : 0;
});

const processedQuestions = computed(() => {
  const value = Number(props.job?.processedQuestions);
  return Number.isFinite(value) === true && value >= 0 ? Math.floor(value) : 0;
});

const createdRules = computed(() => {
  const value = Number(props.job?.createdRules);
  return Number.isFinite(value) === true && value >= 0 ? Math.floor(value) : 0;
});

const skippedRules = computed(() => {
  const value = Number(props.job?.skippedRules);
  return Number.isFinite(value) === true && value >= 0 ? Math.floor(value) : 0;
});

const approvedRulesCount = computed(() => approvedRules.value.length);

const currentAttempt = computed(() => {
  const value = Number(props.job?.currentAttempt);
  return Number.isFinite(value) === true && value >= 0 ? Math.floor(value) : 0;
});

const progressPercent = computed(() => {
  if (totalQuestions.value <= 0) {
    return props.job?.status === 'completed' ? 100 : 0;
  }
  const value = Math.round((processedQuestions.value / totalQuestions.value) * 100);
  if (value > 100) {
    return 100;
  }
  if (value < 0) {
    return 0;
  }
  return value;
});

const currentQuestion = computed(() => typeof props.job?.currentQuestion === 'string' ? props.job.currentQuestion : '');
const currentRule = computed(() => typeof props.job?.currentRule === 'string' ? props.job.currentRule : '');
const currentTests = computed(() => Array.isArray(props.job?.currentTests) === true ? props.job.currentTests : []);
const generatedRules = computed(() => Array.isArray(props.job?.generatedRules) === true ? props.job.generatedRules : []);
const missingKnowledgeRows = computed(() => Array.isArray(props.job?.missingKnowledgeRows) === true ? props.job.missingKnowledgeRows : []);
const diagnosticsLogFile = computed(() => typeof props.job?.diagnosticsLogFile === 'string' ? props.job.diagnosticsLogFile : '');
const failureSummaryEntries = computed(() => {
  const summary = props.job !== null && props.job !== undefined && props.job.failureSummary !== undefined && props.job.failureSummary !== null && typeof props.job.failureSummary === 'object' ? props.job.failureSummary : {};
  const keys = Object.keys(summary);
  const entries = [];
  for (let index = 0; index < keys.length; index = index + 1) {
    const key = keys[index];
    const count = Number(summary[key]);
    if (Number.isFinite(count) !== true || count <= 0) {
      continue;
    }
    entries.push({
      key,
      count,
      label: getFailureSummaryLabel(key)
    });
  }
  return entries;
});
const diagnosticItems = computed(() => {
  const source = Array.isArray(props.job?.diagnostics) === true ? props.job.diagnostics : [];
  const startIndex = source.length > 8 ? source.length - 8 : 0;
  const items = [];
  for (let index = startIndex; index < source.length; index = index + 1) {
    const item = source[index];
    const eventName = typeof item?.eventName === 'string' ? item.eventName : '';
    items.push({
      key: `${index}-${eventName}`,
      title: getDiagnosticTitle(eventName),
      detail: getDiagnosticDetail(item)
    });
  }
  return items;
});
const pendingRules = computed(() => {
  const pending = [];
  for (let index = 0; index < generatedRules.value.length; index = index + 1) {
    const rule = generatedRules.value[index];
    if (approvedRules.value.includes(rule) !== true) {
      pending.push(rule);
    }
  }
  return pending;
});

watch(generatedRules, () => {
  approvedRules.value = [];
});

function approveRules(rules) {
  const source = Array.isArray(rules) === true ? rules : [];
  const approvedNow = [];
  for (let index = 0; index < source.length; index = index + 1) {
    const rule = typeof source[index] === 'string' ? source[index].trim() : '';
    if (rule.length === 0) {
      continue;
    }
    if (approvedRules.value.includes(rule) === true) {
      continue;
    }
    approvedRules.value.push(rule);
    approvedNow.push(rule);
  }
  if (approvedNow.length > 0) {
    emit('approve-rules', approvedNow);
  }
}

function getFailureSummaryLabel(key) {
  if (key === 'accepted') {
    return t('aiChatAgentSetup.advanced.rulesFineTuningFailureAccepted', 'Accepted');
  }
  if (key === 'alreadyCorrectOrDuplicate') {
    return t('aiChatAgentSetup.advanced.rulesFineTuningFailureSkipped', 'Already correct or duplicate');
  }
  if (key === 'correctAnswerNotInTop10Knowledge') {
    return t('aiChatAgentSetup.advanced.rulesFineTuningFailureMissingTop10', 'Correct answer not in top 10 knowledge');
  }
  if (key === 'wrongSourceOrPage') {
    return t('aiChatAgentSetup.advanced.rulesFineTuningFailureWrongSource', 'Wrong source or page');
  }
  if (key === 'candidateRuleIgnored') {
    return t('aiChatAgentSetup.advanced.rulesFineTuningFailureIgnoredRule', 'Candidate rule ignored');
  }
  if (key === 'answerWordingMismatch') {
    return t('aiChatAgentSetup.advanced.rulesFineTuningFailureWording', 'Answer wording mismatch');
  }
  if (key === 'badCandidateRule') {
    return t('aiChatAgentSetup.advanced.rulesFineTuningFailureBadRule', 'Bad candidate rule');
  }
  return t('aiChatAgentSetup.advanced.rulesFineTuningFailureUnknown', 'Unknown rule failure');
}

function getDiagnosticTitle(eventName) {
  if (eventName === 'baselineVariantTested') {
    return t('aiChatAgentSetup.advanced.rulesFineTuningDiagnosticBaseline', 'Baseline variant tested');
  }
  if (eventName === 'candidateVariantTested') {
    return t('aiChatAgentSetup.advanced.rulesFineTuningDiagnosticCandidate', 'Candidate variant tested');
  }
  if (eventName === 'candidateRuleGenerated') {
    return t('aiChatAgentSetup.advanced.rulesFineTuningDiagnosticGenerated', 'Candidate rule generated');
  }
  if (eventName === 'rowRuleResult') {
    return t('aiChatAgentSetup.advanced.rulesFineTuningDiagnosticRowResult', 'Row result');
  }
  if (eventName === 'completedSummary') {
    return t('aiChatAgentSetup.advanced.rulesFineTuningDiagnosticCompleted', 'Completed summary');
  }
  return eventName.length > 0 ? eventName : t('aiChatAgentSetup.advanced.rulesFineTuningDiagnosticUnknown', 'Diagnostic');
}

function appendDiagnosticLine(lines, label, value) {
  const text = typeof value === 'string' ? value.trim() : value !== undefined && value !== null ? String(value).trim() : '';
  if (text.length > 0) {
    lines.push(`${label}: ${text}`);
  }
}

function getDiagnosticDetail(item) {
  const payload = item !== undefined && item !== null && item.payload !== undefined && item.payload !== null && typeof item.payload === 'object' ? item.payload : {};
  const lines = [];
  appendDiagnosticLine(lines, 'Row', payload.rowNumber);
  appendDiagnosticLine(lines, 'Attempt', payload.attempt);
  appendDiagnosticLine(lines, 'Correct', payload.correct === true ? 'true' : payload.correct === false ? 'false' : '');
  appendDiagnosticLine(lines, 'Sources overlap', payload.sourcesOverlap === true ? 'true' : payload.sourcesOverlap === false ? 'false' : '');
  appendDiagnosticLine(lines, 'Status', payload.status);
  appendDiagnosticLine(lines, 'Reason', payload.reason);
  appendDiagnosticLine(lines, 'Live sources', payload.sources);
  appendDiagnosticLine(lines, 'Evidence sources', payload.evidenceSources);
  appendDiagnosticLine(lines, 'Rule', payload.candidateRule !== undefined ? payload.candidateRule : payload.finalRule);
  appendDiagnosticLine(lines, 'Previous failure', payload.previousFailure);
  appendDiagnosticLine(lines, 'Answer', payload.answer);
  if (lines.length === 0) {
    try {
      return JSON.stringify(payload);
    } catch (error) {
      return '';
    }
  }
  return lines.join('\n');
}

const stageLabel = computed(() => {
  const stage = typeof props.job?.stage === 'string' ? props.job.stage : '';
  if (stage === 'extractingQuestions') {
    return t('aiChatAgentSetup.advanced.rulesFineTuningStageExtracting', 'Reading failed Excel questions');
  }
  if (stage === 'initializingAi') {
    return t('aiChatAgentSetup.advanced.rulesFineTuningStageInitializing', 'Initializing AI rule generator');
  }
  if (stage === 'testingKnowledge') {
    return t('aiChatAgentSetup.advanced.rulesFineTuningStageTestingKnowledge', 'Checking that the corrected answer exists in the knowledge base');
  }
  if (stage === 'testingAgentBaseline') {
    return t('aiChatAgentSetup.advanced.rulesFineTuningStageTestingAgentBaseline', 'Testing the agent before creating a rule');
  }
  if (stage === 'creatingRule') {
    return t('aiChatAgentSetup.advanced.rulesFineTuningStageCreating', 'Creating a source rule');
  }
  if (stage === 'testingRule') {
    return t('aiChatAgentSetup.advanced.rulesFineTuningStageTesting', 'Testing the rule');
  }
  if (stage === 'testingAgentAnswer') {
    return t('aiChatAgentSetup.advanced.rulesFineTuningStageTestingAgentAnswer', 'Testing the agent with the candidate rule');
  }
  if (stage === 'ruleSkipped') {
    return t('aiChatAgentSetup.advanced.rulesFineTuningStageSkipped', 'Skipped existing rule');
  }
  if (stage === 'reviewRules') {
    return t('aiChatAgentSetup.advanced.rulesFineTuningStageReview', 'Review generated rules');
  }
  if (stage === 'completed') {
    return t('aiChatAgentSetup.advanced.rulesFineTuningStageCompleted', 'Completed');
  }
  if (stage === 'failed') {
    return t('aiChatAgentSetup.advanced.rulesFineTuningStageFailed', 'Failed');
  }
  return t('aiChatAgentSetup.advanced.rulesFineTuningStageQueued', 'Queued');
});
</script>
